import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useGame } from '../context/GameContext';
import { normalizeTag } from '../game/profile';
import { AvatarChip } from '../components/Avatar';
import { ArrowLeft, Coins, Check, Store, Radio, Sparkles, Award, Lock, Crown, Palette, Trophy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// v3.19 COSMETIC EXPANSION: rarity tier UI meta (kept in sync with backend cosmetics_data.TIER_META).
const TIER_UI = {
  cheap:     { label: 'CHEAP',     color: '#c0c8d8', order: 1 },
  mid:       { label: 'MID',       color: '#5affe0', order: 2 },
  fancy:     { label: 'FANCY',     color: '#ff2ea6', order: 3 },
  whale:     { label: 'WHALE',     color: '#ffd700', order: 4 },
  milestone: { label: 'MILESTONE', color: '#fff2a8', order: 0 },
};
const TIER_ORDER = ['cheap', 'mid', 'fancy', 'whale', 'milestone'];

export default function Marketplace() {
  const { goToMenu, playerName, profile, refreshProfile } = useGame();
  const [tab, setTab] = useState('curses');
  const [tierFilter, setTierFilter] = useState('all'); // v3.19: tier filter chip
  const [curses, setCurses] = useState([]);
  const [frames, setFrames] = useState([]);
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [equipping, setEquipping] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/marketplace/curses`).then(r => setCurses(r.data?.curses || [])).catch(() => setCurses([])),
      axios.get(`${API}/marketplace/frames`).then(r => setFrames(r.data?.frames || [])).catch(() => setFrames([])),
      axios.get(`${API}/marketplace/titles`).then(r => setTitles(r.data?.titles || [])).catch(() => setTitles([])),
    ]).finally(() => setLoading(false));
  }, []);

  const buy = async (endpoint, body, itemId) => {
    setErr('');
    const tag = normalizeTag(playerName);
    if (!tag) { setErr('SET A PLAYER TAG FIRST'); return; }
    setBuying(itemId);
    try {
      await axios.post(`${API}/${endpoint}`, { tag, ...body });
      await refreshProfile();
    } catch (e) {
      setErr(e.response?.data?.detail || 'PURCHASE FAILED');
    } finally {
      setBuying(null);
    }
  };

  const equipFrame = async (frameId) => {
    setErr('');
    const tag = normalizeTag(playerName);
    if (!tag) return;
    setEquipping(frameId);
    try {
      await axios.patch(`${API}/profile/${encodeURIComponent(tag)}`, { equipped_frame: frameId });
      await refreshProfile();
    } catch (e) {
      setErr(e.response?.data?.detail || 'EQUIP FAILED');
    } finally {
      setEquipping(null);
    }
  };

  const equipTitle = async (titleName) => {
    setErr('');
    const tag = normalizeTag(playerName);
    if (!tag) return;
    setEquipping(titleName);
    try {
      await axios.patch(`${API}/profile/${encodeURIComponent(tag)}`, { equipped_title: titleName });
      await refreshProfile();
    } catch (e) {
      setErr(e.response?.data?.detail || 'EQUIP FAILED');
    } finally {
      setEquipping(null);
    }
  };

  const ownedCurses = new Set(profile?.owned_curses || []);
  const ownedFrames = new Set(profile?.owned_frames || []);
  const ownedTitles = new Set(profile?.owned_titles || []);
  const ownedAchievements = new Set(profile?.achievements || []);
  const tokens = profile?.slop_tokens || 0;
  const divines = profile?.divines_collected || 0;
  const equippedFrame = profile?.equipped_frame || null;
  const equippedTitle = profile?.equipped_title || null;

  // v3.19: apply tier filter for the active cosmetics tab (frames/titles). Curses tab ignores filter.
  const filterByTier = (items) => {
    if (tierFilter === 'all') return items;
    return items.filter(i => (i.tier || 'cheap') === tierFilter);
  };
  const filteredFrames = useMemo(() => filterByTier(frames), [frames, tierFilter]);
  const filteredTitles = useMemo(() => filterByTier(titles), [titles, tierFilter]);
  const totalCosmetics = frames.length + titles.length;

  const TABS = [
    { id: 'curses',  label: 'CURSES',  icon: Radio,     color: 'var(--red)'  },
    { id: 'frames',  label: 'FRAMES',  icon: Sparkles,  color: '#fff2a8'     },
    { id: 'titles',  label: 'TITLES',  icon: Crown,     color: 'var(--pink)' },
  ];

  return (
    <div className="min-h-screen w-full px-4 md:px-6 py-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button className="brutal-btn text-sm" onClick={goToMenu} data-testid="marketplace-back-button">
          <ArrowLeft size={16} /> BACK
        </button>
        <h1 className="font-display text-3xl sm:text-5xl font-black uppercase text-[color:var(--neon)] flex items-center gap-3">
          <Store /> SLOP EMPORIUM
        </h1>
        <div className="flex items-center gap-2">
          {profile && (
            <AvatarChip avatar={profile.avatar} frame={equippedFrame} size={36} testid="marketplace-you-avatar" />
          )}
          <div className="brutal-border-pink px-3 py-2 flex items-center gap-2" data-testid="marketplace-tokens">
            <Coins size={16} className="text-[color:var(--pink)]"/>
            <span className="font-display font-black text-xl text-[color:var(--pink)]">{tokens}</span>
          </div>
        </div>
      </div>

      <div className="mt-4 text-center text-xs uppercase tracking-widest text-[color:var(--text-dim)]">
        spend slop tokens · unlock cosmetics · flex on the leaderboard · <span className="text-[color:var(--neon)]" data-testid="marketplace-total-count">{totalCosmetics}</span> items available
      </div>

      {/* Tab switcher */}
      <div className="mt-6 flex justify-center gap-2 flex-wrap">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              className="brutal-btn text-sm px-5 py-2"
              onClick={() => { setTab(t.id); setTierFilter('all'); }}
              style={active ? { borderColor: t.color, color: t.color, boxShadow: `4px 4px 0 ${t.color}` } : { opacity: 0.55 }}
              data-testid={`marketplace-tab-${t.id}`}
            >
              <Icon size={14} className="inline mr-1"/> {t.label}
            </button>
          );
        })}
      </div>

      {/* v3.19: tier filter (frames + titles only) */}
      {(tab === 'frames' || tab === 'titles') && (
        <div className="mt-4 flex justify-center gap-2 flex-wrap" data-testid="marketplace-tier-filter">
          {['all', 'cheap', 'mid', 'fancy', 'whale'].map(t => {
            const meta = TIER_UI[t] || { label: 'ALL', color: '#39ff14' };
            const active = tierFilter === t;
            return (
              <button
                key={t}
                className="brutal-btn text-[10px] px-3 py-1"
                onClick={() => setTierFilter(t)}
                style={active
                  ? { borderColor: meta.color, color: meta.color, boxShadow: `3px 3px 0 ${meta.color}` }
                  : { opacity: 0.5 }}
                data-testid={`marketplace-tier-${t}`}
              >
                {t === 'all' ? 'ALL' : meta.label}
              </button>
            );
          })}
        </div>
      )}

      {err && <div className="mt-4 brutal-border-pink p-3 text-[color:var(--red)] uppercase text-center" data-testid="marketplace-error">{err}</div>}
      {loading && <div className="mt-8 text-center text-[color:var(--text-dim)]">Loading...</div>}

      {/* CURSES TAB */}
      {tab === 'curses' && (
        <div className="mt-8" data-testid="marketplace-curses-tab">
          <div className="text-center text-sm uppercase tracking-widest text-[color:var(--text-dim)] mb-4">
            premium curses · plant on your run codes to torment friends
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {curses.map(c => {
              const isOwned = ownedCurses.has(c.id);
              const canAfford = tokens >= c.price_tokens;
              return (
                <div key={c.id}
                  className="p-4 border-2 bg-[#0a0a0a] flex flex-col gap-2"
                  style={{ borderColor: c.color, boxShadow: `6px 6px 0 ${c.color}` }}
                  data-testid={`marketplace-curse-${c.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-4xl">{c.art}</div>
                    <div className="flex-1">
                      <div className="font-display font-black uppercase text-lg" style={{color: c.color}}>{c.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] italic">"{c.tagline}"</div>
                    </div>
                  </div>
                  <div className="text-sm text-[color:var(--text)]">{c.desc}</div>
                  <div className="flex items-center justify-between mt-2 text-xs uppercase">
                    <div className="flex items-center gap-2">
                      <Radio size={12} className="text-[color:var(--pink)]"/>
                      <span className="text-[color:var(--text-dim)]">USED</span>
                      <span className="text-white font-bold">{c.uses_count || 0}x</span>
                    </div>
                    <div className="text-[color:var(--pink)] font-bold">+{c.tokenBonus} 🪙 ON WIN</div>
                  </div>
                  <button
                    className={`brutal-btn w-full mt-2 text-sm ${isOwned ? 'opacity-70' : ''}`}
                    onClick={() => !isOwned && buy('marketplace/purchase', { curse_id: c.id }, c.id)}
                    disabled={isOwned || !canAfford || buying === c.id}
                    style={{ borderColor: c.color, color: c.color, boxShadow: `4px 4px 0 ${c.color}` }}
                    data-testid={`marketplace-buy-${c.id}`}
                  >
                    {isOwned ? <><Check size={14}/> OWNED</> : buying === c.id ? 'BUYING...' : <><Coins size={14}/> {c.price_tokens} TOKENS</>}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* FRAMES TAB */}
      {tab === 'frames' && (
        <div className="mt-8" data-testid="marketplace-frames-tab">
          <div className="text-center text-sm uppercase tracking-widest text-[color:var(--text-dim)] mb-4">
            avatar frames · <span className="text-white font-bold" data-testid="frames-count">{filteredFrames.length}</span> shown · earn milestone frames by collecting divines · flex-worthy borders visible on every leaderboard row
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFrames.map(f => {
              const isOwned = ownedFrames.has(f.id);
              const isEquipped = equippedFrame === f.id;
              const canAfford = tokens >= f.price_tokens;
              const isMilestone = f.unlock === 'milestone';
              const isAchievement = f.unlock === 'achievement';
              const lockedByMilestone = isMilestone && !isOwned && divines < (f.milestone_divines || 0);
              const lockedByAchievement = isAchievement && !isOwned && !ownedAchievements.has(f.required_achievement);
              const tierMeta = TIER_UI[f.tier] || TIER_UI.cheap;
              return (
                <div key={f.id}
                  className="p-4 border-2 bg-[#0a0a0a] flex flex-col gap-2"
                  style={{ borderColor: f.color, boxShadow: `6px 6px 0 ${f.color}`, opacity: (lockedByMilestone || lockedByAchievement) ? 0.6 : 1 }}
                  data-testid={`marketplace-frame-${f.id}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Preview: avatar chip with the frame applied */}
                    <div className="pt-1">
                      <AvatarChip avatar={profile?.avatar} frame={f.id} size={54} testid={`marketplace-frame-preview-${f.id}`}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-black uppercase text-lg" style={{color: f.color}}>
                          {f.art} {f.name}
                        </div>
                        {f.tier && (
                          <span
                            className="text-[9px] uppercase font-black px-1.5 py-0.5"
                            style={{ border: `1px solid ${tierMeta.color}`, color: tierMeta.color, background: `${tierMeta.color}22` }}
                            data-testid={`marketplace-frame-tier-${f.id}`}
                          >{tierMeta.label}</span>
                        )}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] italic mt-1">
                        {isMilestone ? `MILESTONE · ${f.milestone_divines} DIVINES` :
                         isAchievement ? `ACHIEVEMENT · ${f.required_achievement}` : 'PURCHASE'}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-[color:var(--text)]">{f.desc}</div>
                  {lockedByMilestone && (
                    <div className="text-xs uppercase font-mono tracking-widest text-[color:var(--text-dim)] flex items-center gap-1">
                      <Lock size={12}/> COLLECT {f.milestone_divines - divines} MORE DIVINE{f.milestone_divines - divines === 1 ? '' : 'S'}
                    </div>
                  )}
                  {lockedByAchievement && (
                    <div className="text-xs uppercase font-mono tracking-widest text-[color:var(--red)] flex items-center gap-1" data-testid={`marketplace-frame-ach-lock-${f.id}`}>
                      <Trophy size={12}/> UNLOCK ACHIEVEMENT: {f.required_achievement.replace(/_/g, ' ').toUpperCase()}
                    </div>
                  )}
                  {isOwned ? (
                    isEquipped ? (
                      <button
                        className="brutal-btn w-full mt-2 text-sm"
                        onClick={() => equipFrame('')}
                        disabled={equipping === f.id}
                        style={{ borderColor: 'var(--muted)', color: '#888' }}
                        data-testid={`marketplace-frame-unequip-${f.id}`}
                      >
                        <Check size={14}/> EQUIPPED · CLICK TO UNEQUIP
                      </button>
                    ) : (
                      <button
                        className="brutal-btn w-full mt-2 text-sm"
                        onClick={() => equipFrame(f.id)}
                        disabled={equipping === f.id}
                        style={{ borderColor: f.color, color: f.color, boxShadow: `4px 4px 0 ${f.color}` }}
                        data-testid={`marketplace-frame-equip-${f.id}`}
                      >
                        <Sparkles size={14}/> {equipping === f.id ? 'EQUIPPING...' : 'EQUIP'}
                      </button>
                    )
                  ) : (isMilestone || lockedByAchievement) ? (
                    <button
                      className="brutal-btn w-full mt-2 text-sm"
                      disabled
                      style={{ borderColor: 'var(--muted)', color: '#666' }}
                      data-testid={`marketplace-frame-locked-${f.id}`}
                    >
                      <Lock size={14}/> LOCKED
                    </button>
                  ) : (
                    <button
                      className="brutal-btn w-full mt-2 text-sm"
                      onClick={() => buy('marketplace/purchase_frame', { item_id: f.id }, f.id)}
                      disabled={!canAfford || buying === f.id}
                      style={{ borderColor: f.color, color: f.color, boxShadow: `4px 4px 0 ${f.color}` }}
                      data-testid={`marketplace-frame-buy-${f.id}`}
                    >
                      {buying === f.id ? 'BUYING...' : <><Coins size={14}/> {f.price_tokens} TOKENS</>}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* TITLES TAB */}
      {tab === 'titles' && (
        <div className="mt-8" data-testid="marketplace-titles-tab">
          <div className="text-center text-sm uppercase tracking-widest text-[color:var(--text-dim)] mb-4">
            cosmetic titles · <span className="text-white font-bold" data-testid="titles-count">{filteredTitles.length}</span> shown · displayed under your tag on profile + leaderboard
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTitles.map(t => {
              const isOwned = ownedTitles.has(t.id);
              const isEquipped = equippedTitle === t.name;
              const canAfford = tokens >= t.price_tokens;
              const isAchievement = t.unlock === 'achievement';
              const lockedByAchievement = isAchievement && !isOwned && !ownedAchievements.has(t.required_achievement);
              const tierMeta = TIER_UI[t.tier] || TIER_UI.cheap;
              return (
                <div key={t.id}
                  className="p-4 border-2 bg-[#0a0a0a] flex flex-col gap-2"
                  style={{ borderColor: t.color, boxShadow: `6px 6px 0 ${t.color}`, opacity: lockedByAchievement ? 0.6 : 1 }}
                  data-testid={`marketplace-title-${t.id}`}
                >
                  <div className="flex items-start gap-3">
                    <Crown size={26} style={{ color: t.color }}/>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-black uppercase text-lg" style={{color: t.color}}>{t.name}</div>
                        {t.tier && (
                          <span
                            className="text-[9px] uppercase font-black px-1.5 py-0.5"
                            style={{ border: `1px solid ${tierMeta.color}`, color: tierMeta.color, background: `${tierMeta.color}22` }}
                            data-testid={`marketplace-title-tier-${t.id}`}
                          >{tierMeta.label}</span>
                        )}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] italic">
                        {isAchievement ? `ACHIEVEMENT · ${t.required_achievement}` : `equipped: shown as "${t.name}" under your tag`}
                      </div>
                    </div>
                  </div>
                  {lockedByAchievement && (
                    <div className="text-xs uppercase font-mono tracking-widest text-[color:var(--red)] flex items-center gap-1" data-testid={`marketplace-title-ach-lock-${t.id}`}>
                      <Trophy size={12}/> UNLOCK ACHIEVEMENT: {t.required_achievement.replace(/_/g, ' ').toUpperCase()}
                    </div>
                  )}
                  {isOwned ? (
                    isEquipped ? (
                      <button
                        className="brutal-btn w-full mt-2 text-sm"
                        onClick={() => equipTitle('')}
                        disabled={equipping === t.name}
                        style={{ borderColor: 'var(--muted)', color: '#888' }}
                        data-testid={`marketplace-title-unequip-${t.id}`}
                      >
                        <Check size={14}/> EQUIPPED · CLICK TO UNEQUIP
                      </button>
                    ) : (
                      <button
                        className="brutal-btn w-full mt-2 text-sm"
                        onClick={() => equipTitle(t.name)}
                        disabled={equipping === t.name}
                        style={{ borderColor: t.color, color: t.color, boxShadow: `4px 4px 0 ${t.color}` }}
                        data-testid={`marketplace-title-equip-${t.id}`}
                      >
                        <Crown size={14}/> {equipping === t.name ? 'EQUIPPING...' : 'EQUIP'}
                      </button>
                    )
                  ) : lockedByAchievement ? (
                    <button
                      className="brutal-btn w-full mt-2 text-sm"
                      disabled
                      style={{ borderColor: 'var(--muted)', color: '#666' }}
                      data-testid={`marketplace-title-locked-${t.id}`}
                    >
                      <Lock size={14}/> LOCKED
                    </button>
                  ) : (
                    <button
                      className="brutal-btn w-full mt-2 text-sm"
                      onClick={() => buy('marketplace/purchase_title', { item_id: t.id }, t.id)}
                      disabled={!canAfford || buying === t.id}
                      style={{ borderColor: t.color, color: t.color, boxShadow: `4px 4px 0 ${t.color}` }}
                      data-testid={`marketplace-title-buy-${t.id}`}
                    >
                      {buying === t.id ? 'BUYING...' : <><Coins size={14}/> {t.price_tokens} TOKENS</>}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
